<template>
    <div class="ui-page home-page">
        <ui-page-header title="Visão Geral" :subtitle="currentDate">
            <v-menu offset-y left :close-on-content-click="false" v-model="filterMenu" max-width="360">
                <template v-slot:activator="{ on, attrs }">
                    <v-btn outlined v-bind="attrs" v-on="on">
                        <v-icon left small>mdi-calendar-range</v-icon>
                        {{ rangeLabel }}
                        <v-icon right small>mdi-chevron-down</v-icon>
                    </v-btn>
                </template>
                <v-card>
                    <v-list dense>
                        <v-list-item v-for="preset in presets" :key="preset.id"
                            @click="applyPreset(preset.id)"
                            :class="{ 'preset-active': selectedPreset === preset.id }">
                            <v-list-item-content>
                                <v-list-item-title>{{ preset.name }}</v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-icon v-if="selectedPreset === preset.id">
                                <v-icon small color="primary">mdi-check</v-icon>
                            </v-list-item-icon>
                        </v-list-item>
                    </v-list>

                    <v-divider></v-divider>

                    <div class="pa-3">
                        <div class="ui-label mb-2">Período personalizado</div>
                        <v-row dense>
                            <v-col cols="6">
                                <v-text-field dense outlined hide-details type="date" label="De"
                                    v-model="customStart"></v-text-field>
                            </v-col>
                            <v-col cols="6">
                                <v-text-field dense outlined hide-details type="date" label="Até"
                                    v-model="customEnd"></v-text-field>
                            </v-col>
                        </v-row>
                        <v-btn color="primary" depressed block class="mt-3" small
                            :disabled="!customStart || !customEnd" @click="applyCustom">
                            <v-icon left small>mdi-check</v-icon>
                            Aplicar período
                        </v-btn>
                    </div>
                </v-card>
            </v-menu>
        </ui-page-header>

        <div class="home-stack">
            <!-- Credenciamentos pendentes -->
            <section v-if="pendingAccreditations.loading || pendingAccreditations.total > 0" class="ui-card pending-accreditations-card">
                <template v-if="pendingAccreditations.loading">
                    <div class="d-flex align-center">
                        <v-skeleton-loader type="avatar" class="mr-4" width="44" height="44"></v-skeleton-loader>
                        <div style="flex: 1;">
                            <v-skeleton-loader type="heading" width="260" class="mb-2"></v-skeleton-loader>
                            <v-skeleton-loader type="text" width="420"></v-skeleton-loader>
                        </div>
                    </div>
                </template>

                <div v-else class="pending-accreditations-content">
                    <div class="pending-accreditations-main">
                        <div class="pending-accreditations-icon mr-4">
                            <v-icon size="22">mdi-account-clock</v-icon>
                        </div>
                        <div>
                            <h3 class="ui-section-title">Credenciamentos pendentes de análise</h3>
                            <p class="ui-section-description">Solicitações aguardando aprovação ou rejeição pelo backoffice.</p>
                        </div>
                    </div>

                    <div class="pending-accreditations-side">
                        <div class="text-right">
                            <div class="home-stat__value">{{ pendingAccreditations.total }}</div>
                            <div class="ui-muted ui-small">pendente(s)</div>
                        </div>
                        <v-btn color="primary" depressed @click="goToPendingAccreditations">
                            <v-icon left small>mdi-clipboard-search-outline</v-icon>
                            Analisar agora
                        </v-btn>
                    </div>
                </div>
            </section>

            <!-- Indicadores -->
            <div>
                <div class="d-flex align-center justify-end mb-3">
                    <v-btn-toggle v-model="metricsMode" mandatory dense @change="loadMetrics"
                        background-color="transparent" class="metrics-toggle">
                        <v-btn small value="all" outlined>
                            <v-icon left small>mdi-infinity</v-icon>
                            Total
                        </v-btn>
                        <v-btn small value="range" outlined>
                            <v-icon left small>mdi-calendar-range</v-icon>
                            Período
                        </v-btn>
                    </v-btn-toggle>
                </div>
                <div class="home-grid home-grid--3">
                    <div v-for="(card, index) in metricCards" :key="index" class="ui-card home-stat">
                        <template v-if="loadingMetrics">
                            <v-skeleton-loader type="heading" width="80" class="mb-2"></v-skeleton-loader>
                            <v-skeleton-loader type="text" width="140"></v-skeleton-loader>
                        </template>
                        <template v-else>
                            <div class="d-flex align-start justify-space-between">
                                <div class="home-stat__value">{{ card.value }}</div>
                                <v-icon small class="home-stat__icon">{{ card.icon }}</v-icon>
                            </div>
                            <div class="ui-muted home-stat__label">
                                {{ card.label }}
                                <span v-if="metricsMode === 'range'">&middot; {{ rangeLabel }}</span>
                            </div>
                        </template>
                    </div>
                </div>
            </div>

            <!-- Graficos: Divergencias e Habilitacoes -->
            <div class="home-grid home-grid--2">
                <ui-section :title="`Divergências — ${rangeLabel}`">
                    <template v-if="loadingCharts">
                        <v-skeleton-loader type="image" height="200"></v-skeleton-loader>
                    </template>
                    <highcharts v-else class="hc" :options="lastEventsType1" ref="chart1"></highcharts>
                </ui-section>

                <ui-section :title="`Habilitações — ${rangeLabel}`">
                    <template v-if="loadingCharts">
                        <v-skeleton-loader type="image" height="200"></v-skeleton-loader>
                    </template>
                    <highcharts v-else class="hc" :options="lastEventsType2" ref="chart2"></highcharts>
                </ui-section>
            </div>

            <!-- Prazos -->
            <ui-section title="Prazos para habilitação de crédito" flush>
                <template v-if="loadingDeadlines">
                    <div class="pa-4">
                        <v-skeleton-loader type="table-row" v-for="i in 3" :key="i" class="mb-2"></v-skeleton-loader>
                    </div>
                </template>

                <ui-empty v-else-if="deadlineEvents.length === 0" icon="mdi-calendar-check-outline" text="Nenhum prazo cadastrado" />

                <v-simple-table v-else>
                    <thead>
                        <tr>
                            <th>Caso</th>
                            <th>Progresso</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(item, index) in deadlineEvents" :key="index">
                            <td class="ui-cell-main deadline-name" :title="item.name">{{ item.name }}</td>
                            <td style="width: 40%;">
                                <v-progress-linear
                                    rounded
                                    height="6"
                                    :background-opacity="0.12"
                                    :value="item.totalDiffPercentage"
                                    :color="getDeadlineColor(item.totalDiffPercentage)"
                                ></v-progress-linear>
                            </td>
                            <td style="width: 160px;">
                                <ui-badge :text="getDeadlineChipLabel(item.totalDiff)" :tone="getDeadlineTone(item.totalDiffPercentage)" />
                            </td>
                        </tr>
                    </tbody>
                </v-simple-table>
            </ui-section>

            <!-- Grafico: Usuarios -->
            <ui-section :title="`Usuários cadastrados — ${rangeLabel}`">
                <template v-if="loadingCharts">
                    <v-skeleton-loader type="image" height="200"></v-skeleton-loader>
                </template>
                <highcharts v-else class="hc" :options="lastUsers" ref="chart3"></highcharts>
            </ui-section>
        </div>
    </div>
</template>

<script>
import axios from "axios";

const CHART_COLOR = '#7E55AB';
const BUSINESS_TIME_ZONE = 'America/Sao_Paulo';
const DAY_IN_MS = 24 * 60 * 60 * 1000;

function dateOnlyOrdinal(value) {
    const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;

    return Math.floor(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])) / DAY_IN_MS);
}

function todayOrdinal() {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: BUSINESS_TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(new Date());
    const values = parts.reduce((result, part) => {
        result[part.type] = part.value;
        return result;
    }, {});

    return Math.floor(Date.UTC(Number(values.year), Number(values.month) - 1, Number(values.day)) / DAY_IN_MS);
}

function buildChartConfig(name, categories, data) {
    return {
        title: null,
        chart: { backgroundColor: 'transparent' },
        credits: { enabled: false },
        legend: { enabled: false },
        series: [{ name, data, type: 'column', color: CHART_COLOR, borderRadius: 4 }],
        xAxis: { categories, lineColor: '#E5E7EB', tickColor: '#E5E7EB' },
        yAxis: { title: { text: null }, gridLineColor: '#F3F4F6' },
        plotOptions: { column: { borderWidth: 0 } },
    };
}

export default {
    data: () => ({
        countUsers: 0,
        countEvents: 0,
        countClients: 0,
        deadlineEvents: [],
        lastUsers: buildChartConfig("Usuários", [], []),
        lastEventsType1: { title: null },
        lastEventsType2: { title: null },

        loadingMetrics: true,
        loadingCharts: true,
        loadingDeadlines: true,
        pendingAccreditations: {
            loading: true,
            total: 0,
            items: [],
        },

        metricsMode: 'all',
        filterMenu: false,
        selectedPreset: 'last7',
        customStart: null,
        customEnd: null,
        rangeStart: null,
        rangeEnd: null,

        presets: [
            { id: 'today', name: 'Hoje' },
            { id: 'yesterday', name: 'Ontem' },
            { id: 'last7', name: 'Últimos 7 dias' },
            { id: 'last15', name: 'Últimos 15 dias' },
            { id: 'last30', name: 'Últimos 30 dias' },
            { id: 'thisMonth', name: 'Este mês' },
            { id: 'lastMonth', name: 'Mês passado' },
            { id: 'last3Months', name: 'Últimos 3 meses' },
        ],
    }),

    computed: {
        currentDate() {
            const date = this.$moment();
            const formatted = date.format('dddd, D [de] MMMM [de] YYYY');
            return formatted.charAt(0).toUpperCase() + formatted.slice(1);
        },

        rangeLabel() {
            if (this.selectedPreset === 'custom') {
                const s = this.$moment(this.rangeStart).format('DD/MM/YYYY');
                const e = this.$moment(this.rangeEnd).format('DD/MM/YYYY');
                return `${s} — ${e}`;
            }
            const preset = this.presets.find(p => p.id === this.selectedPreset);
            return preset ? preset.name : 'Últimos 7 dias';
        },

        metricCards() {
            return [
                { value: this.countUsers, label: 'Credores cadastrados', icon: 'mdi-account-group', iconBg: '#EEF2FF', iconColor: '#2b3985' },
                { value: this.countClients, label: 'Devedoras cadastradas', icon: 'mdi-domain', iconBg: '#F3E8FF', iconColor: '#7E55AB' },
                { value: this.countEvents, label: 'Hab/Div pendentes', icon: 'mdi-clock-alert-outline', iconBg: '#FFF7ED', iconColor: '#F59E0B' },
            ];
        },
    },

    created() {
        this.applyPreset('last7', false);
        this.loadDashboard();
        this.loadPendingAccreditations();
    },

    methods: {
        resolveRange(presetId) {
            const now = this.$moment();
            let start, end;

            switch (presetId) {
                case 'today':
                    start = now.clone().startOf('day'); end = now.clone().endOf('day'); break;
                case 'yesterday':
                    start = now.clone().subtract(1, 'day').startOf('day'); end = now.clone().subtract(1, 'day').endOf('day'); break;
                case 'last7':
                    start = now.clone().subtract(6, 'days').startOf('day'); end = now.clone().endOf('day'); break;
                case 'last15':
                    start = now.clone().subtract(14, 'days').startOf('day'); end = now.clone().endOf('day'); break;
                case 'last30':
                    start = now.clone().subtract(29, 'days').startOf('day'); end = now.clone().endOf('day'); break;
                case 'thisMonth':
                    start = now.clone().startOf('month'); end = now.clone().endOf('month'); break;
                case 'lastMonth':
                    start = now.clone().subtract(1, 'month').startOf('month'); end = now.clone().subtract(1, 'month').endOf('month'); break;
                case 'last3Months':
                    start = now.clone().subtract(3, 'months').startOf('day'); end = now.clone().endOf('day'); break;
                default:
                    start = now.clone().subtract(6, 'days').startOf('day'); end = now.clone().endOf('day');
            }

            return { start: start.format('YYYY-MM-DD'), end: end.format('YYYY-MM-DD') };
        },

        applyPreset(presetId, reload = true) {
            this.selectedPreset = presetId;
            const { start, end } = this.resolveRange(presetId);
            this.rangeStart = start;
            this.rangeEnd = end;
            this.filterMenu = false;
            if (reload) this.loadDashboard();
        },

        applyCustom() {
            if (!this.customStart || !this.customEnd) return;
            this.selectedPreset = 'custom';
            this.rangeStart = this.customStart;
            this.rangeEnd = this.customEnd;
            this.filterMenu = false;
            this.loadDashboard();
        },

        buildDateCategories() {
            const categories = [];
            const start = this.$moment(this.rangeStart);
            const end = this.$moment(this.rangeEnd);
            const days = end.diff(start, 'days');

            for (let i = 0; i <= days; i++) {
                categories.push(start.clone().add(i, 'days').format('DD/MM/YYYY'));
            }

            return categories;
        },

        async loadDashboard() {
            this.loadingMetrics = true;
            this.loadingCharts = true;
            this.loadingDeadlines = true;

            await Promise.all([
                this.loadMetrics(),
                this.loadCharts(),
                this.loadDeadlineEvents(),
            ]);
        },

        async loadPendingAccreditations() {
            this.pendingAccreditations.loading = true;

            try {
                const result = await axios.get('/api/admin/accreditations/pending-summary', {
                    params: {
                        limit: 5,
                    },
                });

                const content = result.data.content || {};
                this.pendingAccreditations.items = content.data || [];
                this.pendingAccreditations.total = content.total || 0;
            } catch (error) {
                this.pendingAccreditations.items = [];
                this.pendingAccreditations.total = 0;
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.pendingAccreditations.loading = false;
            }
        },

        async loadMetrics() {
            this.loadingMetrics = true;
            await Promise.all([
                this.loadCount('/api/admin/dashboard/count-users', 'countUsers'),
                this.loadCount('/api/admin/dashboard/count-clients', 'countClients'),
                this.loadCount('/api/admin/dashboard/count-events', 'countEvents'),
            ]);
            this.loadingMetrics = false;
        },

        async loadCharts() {
            this.loadingCharts = true;
            await Promise.all([
                this.loadLastUsers(),
                this.loadLastEvents(),
            ]);
            this.loadingCharts = false;
        },

        async loadCount(url, field) {
            try {
                const params = this.metricsMode === 'range'
                    ? { start: this.rangeStart, end: this.rangeEnd }
                    : {};
                const result = await axios.get(url, { params });
                this[field] = result.data.content;
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            }
        },

        async loadLastUsers() {
            try {
                const result = await axios.get('/api/admin/dashboard/last-users', {
                    params: { start: this.rangeStart, end: this.rangeEnd }
                });
                const categories = this.buildDateCategories();
                const data = new Array(categories.length).fill(0);

                for (const item of result.data.content) {
                    const date = this.$moment(item.date).format("DD/MM/YYYY");
                    const index = categories.indexOf(date);
                    if (index !== -1) data[index] = item.total;
                }

                this.lastUsers = buildChartConfig("Usuários", categories, data);
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            }
        },

        async loadLastEvents() {
            try {
                const result = await axios.get('/api/admin/dashboard/last-events', {
                    params: { start: this.rangeStart, end: this.rangeEnd }
                });
                const categories = this.buildDateCategories();
                const dataType1 = new Array(categories.length).fill(0);
                const dataType2 = new Array(categories.length).fill(0);

                for (const item of result.data.content) {
                    const date = this.$moment(item.date).format("DD/MM/YYYY");
                    const index = categories.indexOf(date);
                    if (index !== -1) {
                        dataType1[index] = item.total_type1;
                        dataType2[index] = item.total_type2;
                    }
                }

                this.lastEventsType1 = buildChartConfig("Divergências", categories, dataType1);
                this.lastEventsType2 = buildChartConfig("Habilitações", categories, dataType2);
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            }
        },

        async loadDeadlineEvents() {
            try {
                const result = await axios.get('/api/admin/dashboard/deadline-events');
                const data = result.data.content;

                if (!Array.isArray(data)) {
                    this.deadlineEvents = [];
                    return;
                }

                const currentDay = todayOrdinal();
                this.deadlineEvents = data.map(item => {
                    const startDay = dateOnlyOrdinal(item.start_event_at);
                    const endDay = dateOnlyOrdinal(item.end_event_at);
                    const duration = Math.max((endDay || 0) - (startDay || 0), 0);
                    const totalDiff = Math.max((endDay || currentDay) - currentDay, 0);
                    const elapsed = Math.min(Math.max(currentDay - (startDay || currentDay), 0), duration);
                    const totalDiffPercentage = duration > 0 ? (100 * elapsed) / duration : 100;

                    return { ...item, totalDiff, duration, totalDiffPercentage };
                });
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.loadingDeadlines = false;
            }
        },

        getDeadlineColor(percentage) {
            if (percentage <= 50) return '#86EFAC';
            if (percentage <= 80) return '#FCD34D';
            return '#FCA5A5';
        },

        getDeadlineChipColor(percentage) {
            if (percentage <= 50) return '#F0FDF4';
            if (percentage <= 80) return '#FFFBEB';
            return '#FEF2F2';
        },

        getDeadlineChipTextColor(percentage) {
            if (percentage <= 50) return '#16A34A';
            if (percentage <= 80) return '#D97706';
            return '#DC2626';
        },

        getDeadlineTone(percentage) {
            if (percentage <= 50) return 'success';
            if (percentage <= 80) return 'warning';
            return 'danger';
        },

        getDeadlineChipLabel(totalDiff) {
            if (totalDiff === 0) return 'Último dia';
            if (totalDiff <= 7) return `${totalDiff}d restantes`;
            return 'Dentro do prazo';
        },

        goToPendingAccreditations() {
            this.$router.push('/credenciamentos');
        },
    },
};
</script>


<style lang="scss" scoped>
@import "../assets/scss/ui/tokens";

// blocos empilhados com espacamento uniforme
.home-stack {
    display: grid;
    gap: 16px;

    > *,
    .home-grid > * {
        margin-top: 0 !important;
    }
}

.home-grid {
    display: grid;
    gap: 16px;
    grid-template-columns: 1fr;
    min-width: 0;

    > * {
        min-width: 0;
    }
}

@media (min-width: 600px) {
    .home-grid--3 {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (min-width: 960px) {
    .home-grid--3 {
        grid-template-columns: repeat(3, 1fr);
    }

    .home-grid--2 {
        grid-template-columns: repeat(2, 1fr);
    }
}

.preset-active {
    background-color: $ui-bg-muted;
}

.home-stat {
    padding: 18px 20px;
}

.home-stat__value {
    font-size: 28px;
    font-weight: 600;
    line-height: 1.15;
    letter-spacing: -0.02em;
    color: $ui-fg;
}

.home-stat__label {
    font-size: 13px;
    margin-top: 4px;
}

.home-stat__icon {
    color: $ui-subtle !important;
}

.pending-accreditations-card {
    padding: 18px 20px;
    border-left: 4px solid $ui-accent;
}

.pending-accreditations-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
}

.pending-accreditations-main {
    display: flex;
    align-items: center;
    min-width: 0;
    flex: 1;
}

.pending-accreditations-icon {
    width: 44px;
    height: 44px;
    border-radius: $ui-radius-sm;
    background-color: $ui-accent-soft;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    .v-icon {
        color: $ui-accent !important;
    }
}

.pending-accreditations-side {
    display: flex;
    align-items: center;
    gap: 20px;
    flex-shrink: 0;
}

@media (max-width: 960px) {
    .pending-accreditations-content {
        align-items: stretch;
        flex-direction: column;
    }

    .pending-accreditations-side {
        justify-content: space-between;
    }
}

.metrics-toggle {
    background-color: transparent !important;
}

.hc {
    width: 100%;
}

.deadline-name {
    max-width: 280px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>
